// ============================================================================
// Meal Forecast report helpers (buffet check-off slice 4) — pure, unit-tested
// ============================================================================
// The forecast arrives day by day with three meals each; the report grid is one
// row per date and meal. Adults and children are written "12+3" so the grid
// stays narrow; plans are written "AP 23 · MAP 40*" (* = guest's choice).

import type { MealForecast, MealForecastMeal } from 'api/types/mealForecast.types';

export const MEALS = [
  { key: 'breakfast', label: 'Breakfast' },
  { key: 'lunch', label: 'Lunch' },
  { key: 'dinner', label: 'Dinner' },
] as const;

export interface MealForecastRow {
  id: string;
  date: string;
  meal: string;
  inHouse: number;
  inHouseText: string;
  arriving: number;
  arrivingText: string;
  tentative: number;
  tentativeText: string;
  extra: number;
  total: number;
  mayCome: number;
  served: number;
  plans: string;
}

/** "12+3"; "12" when no children. */
export const paxText = (adults: number, children: number): string => (children > 0 ? `${adults}+${children}` : `${adults}`);

export const plansText = (m: MealForecastMeal): string =>
  m.composition.map((c) => `${c.planCode} ${c.pax}${c.isGuestChoice ? '*' : ''}`).join(' · ');

/** One row per date and meal, in date then meal order; meals with nothing at all are left out. */
export function forecastRows(forecast: MealForecast | null): MealForecastRow[] {
  if (!forecast) return [];
  return forecast.series.flatMap((day) => MEALS.map(({ key, label }) => {
    const m = day[key];
    return {
      id: `${day.date}-${key}`,
      date: day.date,
      meal: label,
      inHouse: m.inHouseAdults + m.inHouseChildren,
      inHouseText: paxText(m.inHouseAdults, m.inHouseChildren),
      arriving: m.arrivingAdults + m.arrivingChildren,
      arrivingText: paxText(m.arrivingAdults, m.arrivingChildren),
      tentative: m.tentativeAdults + m.tentativeChildren,
      tentativeText: paxText(m.tentativeAdults, m.tentativeChildren),
      extra: m.extraPax,
      total: m.total,
      mayCome: m.mayComeAdults + m.mayComeChildren,
      served: m.servedAdults + m.servedChildren,
      plans: plansText(m),
    };
  })).filter((r) => r.total + r.mayCome + r.served > 0);
}

/** Covers per meal over the whole range (for the totals band). */
export function forecastTotals(forecast: MealForecast | null): Record<string, number> {
  const totals: Record<string, number> = { Breakfast: 0, Lunch: 0, Dinner: 0 };
  for (const day of forecast?.series ?? []) {
    for (const { key, label } of MEALS) totals[label] += day[key].total;
  }
  return totals;
}
